import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { get, pageContext, getRulesFromState, executeSelector } from 'ctm-util-functions';
import { quoteFormSelector, resultsConfigSelector } from 'ctm-selectors';

import { FormattedInput } from '../../form/formatted_input/formatted_input';

import { onChange } from '../utils';
import { SimpleFlexbox } from '../../flexbox/flexbox';

import styles from './radio_filter.css';

class _RadioFilter extends Component {

    constructor(props) {
        super(props);

        this.formElementFactory = pageContext.getProps().factories.formElement;
        this.radioSchema = this.getRadioSchema();
        this.inputSchema = this.getInputSchema();

        // ignoreIsOriginalValueSelected - to fix the bug with "Level of insurance" selector on car-insurance page. CTM-3772
        const { extraActionsFunction, filterProps, initialFormValue, setFilterActionsOnChange, usageValues, ignoreIsOriginalValueSelected } = props;

        this.eventProps = {
            onClickFunction: (e) => {
                // Flag to indicate if the original value is selected in radio selectors
                const isOriginalValueSelected = filterProps.values.length > 2 && e.target.value === props.selectedValue;
                const showUpdateButton = (e.target.value !== 'C' && !isOriginalValueSelected) || ignoreIsOriginalValueSelected;
                if (extraActionsFunction && (!isOriginalValueSelected || ignoreIsOriginalValueSelected)) {
                    extraActionsFunction(e);
                }
                onChange(e, props, showUpdateButton);
                // this logic is required for the householdType to be set when usage filters are changed on the results page on energy
                if (setFilterActionsOnChange && usageValues && showUpdateButton) {
                    const value = usageValues[e.target.value];
                    if (!value) return;
                    props.dispatch({
                        type: 'SET_FILTER_ACTION',
                        value,
                        formStateKey: filterProps.initialFormStateKey,
                        originalValue: initialFormValue,
                        resultsStagedStateKey: filterProps.initialFormStateKey
                    });
                }
                if (e.target.value === 'C') {
                    props.dispatch({
                        type: 'SET_VALUE',
                        value: e.target.value,
                        identifier: 'gas.householdType',
                        stateKey: 'vertical.quote'
                    });
                }
            }
        };
    }

    /**
     * Return the schema for the stacked RadioButtonGroup
     * @returns {Object} - The RadioButtonGroupSchema
     */
    getRadioSchema = () => {
        const { filterProps } = this.props;
        const { filter, values, UIComponent = 'radio_button_group_vertical_small_rounded' } = filterProps;
        if (!values) {
            return null;
        }

        return {
            variant: 'stacked',
            field: {
                type: 'radiogroup',
                metaType: 'radio',
                UIComponent,
                values
            },
            identifier: filter,
            stateKey: 'notRequired'
        };
    };

    /**
     * Return the schema for the input for custom values
     * @returns {Object} - The schema for the input
     */
    getInputSchema = () => {
        const { filterProps } = this.props;
        const { customInputSuffix, customInputIdentifier } = filterProps;
        return {
            field: {
                type: 'formatted',
                allowDecimal: true,
                decimalLimit: 2,
                metaType: 'tel',
                suffix: customInputSuffix,
                maxlength: '9',
                UIComponent: 'input_small'
            },
            identifier: customInputIdentifier,
            stateKey: 'notRequired'
        };
    };

    /**
     * Determine if the component should re-render based on the nextState
     * @param  {Object} nextProps  The incoming set of props
     * @returns {Boolean} true if the component should re-render
     */
    shouldComponentUpdate = (nextProps) => {
        const { selectedValue } = this.props;
        return selectedValue !== nextProps.selectedValue;
    };

    /**
     * Return the Flexbox with the custom input if filterProps.showCustomInput is true
     * @returns {Node|Null} - Return the Flexbox with the custom input if it has been specified, otherwise, return null
     */
    getCustomInput = () => {
        const { filterProps, dispatch, customValue } = this.props;
        const {
            showCustomInput,
            filter,
            resultsStagedStateKey,
            fetchNewResults,
            resultsStateKey,
            formStateKey
        } = filterProps;

        if (!showCustomInput) {
            return null;
        }
        const inputOnChange = (event, value) => {
            const actions = [
                {
                    key: 'filters.showFiltersUpdateButton',
                    value: true
                },
                {
                    key: resultsStagedStateKey,
                    value
                },
                {
                    key: 'filters.fetchNewResults',
                    value: fetchNewResults
                }
            ];
            dispatch({
                type: 'SET_RESULTS_CONFIG_VALUES',
                actions
            });

            dispatch({
                type: 'SET_FILTER_VALUE',
                filter,
                filterStaged: true,
                filterValue: value
            });

            dispatch({
                type: 'SET_FILTER_ACTION',
                resultsStateKey,
                resultsStagedStateKey,
                formStateKey
            });

            if (filter === 'customDailyGasConsumption') {
                dispatch({
                    type: 'SET_VALUE',
                    value: 'C',
                    identifier: 'gas.householdType',
                    stateKey: 'vertical.quote'
                });
            }
        };

        return (
            <SimpleFlexbox display="flex" xs={8} column alignSelfEnd="xs">
                <FormattedInput schema={this.inputSchema} dispatch={dispatch} rules={{}} touched={false} value={customValue || ''} onChange={inputOnChange} />
            </SimpleFlexbox>
        );
    };

    /**
     * Render a Radio Button Group if 'filterProps.values' has at lease one option.
     * @returns {Node|Null} - Return a Radio Button Group.
     */
    renderRadioGroup = () => {
        const { filterProps, selectedValue = null, dispatch } = this.props;

        if (filterProps.values && filterProps.values.length > 0) {
            return this.formElementFactory.createElement(
                this.radioSchema,
                {},
                false,
                dispatch,
                selectedValue,
                null,
                null,
                { eventProps: this.eventProps }
            );
        }
        return null;
    };

    /**
     * Render the RadioButtonGroup component.
     * @returns {Node} The RadioButtonGroup menu.
     */
    render = () => {
        const { labelCssClass, subLabel, rules, label } = this.props;
        const labelClassList = classNames(styles.label_span, labelCssClass ? styles[labelCssClass] : false);

        if (!get(rules, 'visibility.result', true)) {
            return null;
        }
        return (
            <>
                <SimpleFlexbox display="flex">
                    <span className={labelClassList}>{label}</span>
                </SimpleFlexbox>
                <SimpleFlexbox display="flex" padding="bottom">
                    <SimpleFlexbox display="flex" column>
                        {subLabel && <span className={styles.sub_label_span}>{subLabel}</span>}
                        {this.renderRadioGroup()}
                    </SimpleFlexbox>
                    {this.getCustomInput()}
                </SimpleFlexbox>
            </>
        );
    };

}

_RadioFilter.propTypes = {
    filterProps: PropTypes.shape({
        filter: PropTypes.string,
        label: PropTypes.string,
        showCustomInput: PropTypes.bool,
        customInputIdentifier: PropTypes.string,
        customInputSuffix: PropTypes.string,
        showUpdateButton: PropTypes.bool,
        fetchNewResults: PropTypes.bool.isRequired,
        resultsStagedStateKey: PropTypes.string.isRequired,
        resultsStateKey: PropTypes.string.isRequired,
        formStateKey: PropTypes.string.isRequired,
        initialFormStateKey: PropTypes.string,
        UIComponent: PropTypes.string,
        values: PropTypes.arrayOf(
            PropTypes.shape({
                value: PropTypes.string,
                label: PropTypes.string,
                default: PropTypes.bool
            })
        ).isRequired
    }).isRequired,
    usageValues: PropTypes.objectOf(PropTypes.string),
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    subLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    labelCssClass: PropTypes.string,
    rules: PropTypes.object,
    selectedValue: PropTypes.string,
    customValue: PropTypes.string,
    extraActionsFunction: PropTypes.func,
    dispatch: PropTypes.func,
    initialFormValue: PropTypes.string,
    setFilterActionsOnChange: PropTypes.bool,
    ignoreIsOriginalValueSelected: PropTypes.bool
};

/**
 * mapStateToProps - sets up the rules required by this component
 * Exported for unit tests only
 * @param {Object} state - The entire application state
 * @param {Object} props - The props for the form group this is being applied to
 * @returns {Object}     - Props for the component, mapped from state values
 */
export const mapStateToProps = (state, props) => {
    // @todo: figure how to test this without creating 300 mocks
    const rules = getRulesFromState(state, props);

    const resultsConfig = resultsConfigSelector(state);
    const form = quoteFormSelector(state);

    // get the resultsStagedStateKey and the initialFormStateKey from props
    // not all radioFilters will have a initialFormStateKey or resultsStagedStateKey, so use empty string defaults
    const { resultsStagedStateKey = '', initialFormStateKey = '' } = props.filterProps;

    // if a resultsStagedStateKey has been specified, get the value from results config state
    // if this is the initial mount of the filter (eg, first time results has been loaded), the value might not have been set yet
    // so default to an empty string in this case
    const stagedValue = resultsStagedStateKey ? resultsConfig.getIn(resultsStagedStateKey.split('.')) : '';
    // if an initialFormStateKey has been specified, get the value from form state, sometimes the value for the radio filter
    // won't already be set in the form state, so default to an empty string in this case
    const initialFormValue = initialFormStateKey ? form.getIn(initialFormStateKey.split('.')) : '';

    // The value that should be selected by default when the filter is mounted or re-rendered, always go for the staged results value
    // first, if this isn't set use the initialFormValue
    let selectedValue = stagedValue || initialFormValue;
    if (props.filterProps.selector) {
        // Run any selectors on the selectedValue, if selectors have been defined for this filter
        selectedValue = executeSelector(props.filterProps.selector, state, selectedValue);
    }

    // some RadioFilters allow for a custom value that is provided via an input
    let customValue = '';
    if (props.filterProps.showCustomInput && selectedValue === 'C') {
        // if this filter is showing a custom input and the custom radio has been selected, get the custom value for the input
        customValue = resultsConfig.getIn(props.filterProps.customInputIdentifier.split('.'));
    }

    return {
        selectedValue,
        customValue,
        rules,
        initialFormValue
    };
};

export { _RadioFilter };
export const RadioFilter = connect(mapStateToProps)(_RadioFilter);
